import { useEffect } from 'react';
import { chatSend } from '../../../assets/images/images';
import { useSelectionState } from '../../../hooks/useSelectionState';
import { useHunterProseTypewriter } from '../../../hooks/useHunterProseTypewriter';
import OptionButton from './shared/OptionButton';
import MessageContainer from './shared/MessageContainer';
import { IncomingMessageAIProps } from './shared/types';

const DEFAULT_INITIAL =
  "Welcome! Let's build your job description. What position are you creating this for? Feel free to share the job title and any key details you have in mind.";

const AI_SCROLL_SELECTOR = '.chat-with-ai .mesgs';

/**
 * IncomingMessageAI - For ChatWithAI (General chat/Job creation)
 * Features:
 * - Show all options without prioritization
 * - Preserve API order
 * - No "More/Less" toggle
 * - Simple selection logic
 * - Letter-by-letter reveal for the latest Hunter reply
 */
const IncomingMessageAI = ({
    chat,
    message,
    isLoading,
    isFromJob = false,
    setLastMessageId,
    handleSendMessage,
    setEnableGenerateResponse,
    setLastQuestionStep,
    isLastMessage,
}: IncomingMessageAIProps) => {
    const isMultiSelect = chat?.is_multi_select;
    const {
        selectedOptions,
        optionKeys,
        renderKey,
        handleOptionClick,
        setSelectionsFromJSON
    } = useSelectionState(isMultiSelect);

    const {
        messageKind,
        displayedText,
        isComplete,
        revealedBullets,
        revealedFinish,
        showOptions,
        renderCaret,
    } = useHunterProseTypewriter({
        message,
        chatId: chat?.id,
        isLastMessage,
        scrollSelector: AI_SCROLL_SELECTOR,
        defaultInitial: DEFAULT_INITIAL,
        supportSuggestions: false,
    });

    // Sync selections from chat.select_json
    useEffect(() => {
        setSelectionsFromJSON(chat?.select_json);
    }, [chat?.select_json, chat?.id, setSelectionsFromJSON]);

    // Set last message ID and enable response
    useEffect(() => {
        if (chat.id && !chat?.is_local && typeof chat.id === 'number') {
            setLastMessageId(chat.id);
        }
        if (chat?.step && typeof setLastQuestionStep === 'function') {
            setLastQuestionStep(chat?.step);
        }
        // Backend may show the finish summary (result.step === 'finish') without is_finished.
        // Treat either signal as ready-to-generate so the button matches what Hunter shows.
        const isFinished = Boolean(
            chat?.is_finished ||
            message?.step === 'finish' ||
            chat?.result?.step === 'finish' ||
            chat?.step === 'finish'
        );
        if (isFinished) {
            setEnableGenerateResponse(true);
        }
    }, [chat, message, setLastQuestionStep, setLastMessageId, setEnableGenerateResponse]);

    const handleMultiClick = () => {
        if (selectedOptions.length === 0) return;

        if (!isMultiSelect && selectedOptions.length === 1) {
            const option = selectedOptions[0];
            const key = optionKeys.get(option);

            if (key) {
                handleSendMessage({ [key]: option }, option);
            } else {
                handleSendMessage([option], option);
            }
        } else {
            handleSendMessage(selectedOptions, '');
        }
    };

    const renderOptions = () => {
        // Disable options ONLY if it's not the last message.
        const isDisabled = !isLastMessage;

        if (message?.title && !Array.isArray(message?.title) && typeof message?.title === 'object' && message.title !== null) {
            // Object-based titles - show all, preserve order
            const entries = Object.entries(message.title)
                .filter(([key]) => key !== 'could_have')
                .filter(([, value]) => !(value as string).toLowerCase().includes('what makes you a great candidate'));

            return (
                <>
                    {entries.map(([key, value], index: number) => {
                        const isSelected = selectedOptions.includes(value as string);
                        return (
                            <OptionButton
                                key={`${key}-${index}`}
                                value={value as string}
                                keyProp={key}
                                index={index}
                                isSelected={isSelected}
                                renderKey={renderKey}
                                isDisabled={isDisabled}
                                onClick={handleOptionClick}
                            />
                        );
                    })}
                </>
            );
        } else if (message?.title && Array.isArray(message?.title)) {
            // Array-based titles - show all, preserve API order
            const options = message.title
                .filter((option: string) => option !== 'Could-Haves')
                .filter((option: string) => !option.toLowerCase().includes('what makes you a great candidate'));

            return (
                <>
                    {options.map((option: string, index: number) => {
                        const isSelected = selectedOptions.includes(option);
                        return (
                            <OptionButton
                                key={`${option}-${index}`}
                                value={option}
                                keyProp={null}
                                index={index}
                                isSelected={isSelected}
                                renderKey={renderKey}
                                isDisabled={isDisabled}
                                onClick={handleOptionClick}
                            />
                        );
                    })}
                </>
            );
        }
        return null;
    };

    const renderMessageContent = () => {
        if (messageKind === 'empty') {
            return <p><span className="inline-bold-text">Hunter: </span></p>;
        }

        if (messageKind === 'finish' && revealedFinish) {
            return (
                <p>
                    <span className="inline-bold-text">Hunter:</span>
                    {' '}
                    <span>
                        {revealedFinish.introText}
                        {renderCaret(revealedFinish.caretIn === 'intro')}
                    </span>
                    {(revealedFinish.entries.length > 0 ||
                        revealedFinish.closingText ||
                        revealedFinish.caretIn === 'closing') && (
                        <><br /><br /></>
                    )}
                    {revealedFinish.entries.map((entry, index) => (
                        <div key={entry.key} className="final-received">
                            <span className="inline-medium-text">
                                {entry.label}
                                {entry.showColon ? ':' : ''}
                            </span>
                            {entry.showColon ? ` ${entry.value}` : ''}
                            {renderCaret(
                                revealedFinish.caretIn === 'entry' &&
                                    revealedFinish.caretEntryIndex === index,
                            )}
                        </div>
                    ))}
                    {(revealedFinish.closingText || revealedFinish.caretIn === 'closing') && (
                        <>
                            <br />
                            <span>
                                {revealedFinish.closingText}
                                {renderCaret(revealedFinish.caretIn === 'closing')}
                            </span>
                        </>
                    )}
                </p>
            );
        }

        if (revealedBullets && (revealedBullets.bulletItems.length > 0 || revealedBullets.introText || !isComplete)) {
            return (
                <p>
                    <span className="inline-bold-text">Hunter: </span>
                    {(revealedBullets.introText || revealedBullets.caretIn === 'intro') && (
                        <>
                            <span>
                                {revealedBullets.introText}
                                {renderCaret(revealedBullets.caretIn === 'intro')}
                            </span>
                            {(revealedBullets.bulletItems.length > 0 || isComplete) && <br />}
                        </>
                    )}
                    {revealedBullets.bulletItems.length > 0 && (
                        <ul className="message-list">
                            {revealedBullets.bulletItems.map((item: string, index: number) => (
                                <li key={index}>
                                    {item}
                                    {renderCaret(
                                        revealedBullets.caretIn === 'bullet' &&
                                            revealedBullets.caretBulletIndex === index,
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                    {(revealedBullets.closingText || revealedBullets.caretIn === 'closing') && (
                        <span>
                            {revealedBullets.closingText}
                            {renderCaret(revealedBullets.caretIn === 'closing')}
                        </span>
                    )}
                </p>
            );
        }

        return (
            <p>
                <span className="inline-bold-text">Hunter: </span>
                <span className="chat-typewriter-text">
                    {displayedText}
                    {renderCaret(true)}
                </span>
            </p>
        );
    };

    return (
        <>
            <MessageContainer isFromJob={isFromJob}>
                {renderMessageContent()}
            </MessageContainer>

            {showOptions && (
                    <div className={`chat-single-option no-image-option ${isLoading ? 'p-none' : ''}`}>
                        {renderOptions()}
                        {(!chat?.select_json || isLastMessage) && (
                            <img
                                src={chatSend}
                                alt="send"
                                className={`chat-send-btn ${selectedOptions?.length > 0 ? '' : 'p-none'}`}
                                onClick={handleMultiClick}
                                role="button"
                                aria-label="Send selected options"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleMultiClick();
                                    }
                                }}
                            />
                        )}
                    </div>
                )}
        </>
    );
};

export default IncomingMessageAI;
